import store from '@/store';
import { handleUnauthorized } from '@/common/auth';
import { attachMetadata } from '@/common/network';

async function parseResponseBodySafe(response) {
    const clonedResponse = response.clone();

    try {
        return await response.json();
    } catch {
        try {
            return await clonedResponse.text();
        } catch {
            return null;
        }
    }
}

function getErrorMessage(response, responseBody) {
    if (responseBody && typeof responseBody === 'object') {
        return responseBody.message || response.statusText || String(response.status);
    }

    return responseBody || response.statusText || String(response.status);
}

function getErrorCode(response, responseBody) {
    if (responseBody && typeof responseBody === 'object') {
        return responseBody.code || responseBody.error_code || responseBody.errorCode || response.status;
    }

    return response.status;
}

function createResponseMetadata(response, responseBody) {
    return {
        url: response.url,
        status: response.status,
        statusText: response.statusText,
        code: getErrorCode(response, responseBody),
        body: responseBody,
    };
}

function createApiError(message, response, responseBody) {
    const error = new Error(message);
    error.code = getErrorCode(response, responseBody);
    error.status = response.status;
    attachMetadata(error, 'requestMetadata', response.requestMetadata);
    attachMetadata(error, 'responseMetadata', createResponseMetadata(response, responseBody));
    return error;
}

export async function handleApiResponse(response, options = {}) {
    const {
        unauthorized = 'logout',
        expectSuccess = false,
        successErrorMessage = 'Unsuccessful response',
        clearMaintenance = true,
    } = options;

    if (response.ok) {
        if (clearMaintenance) {
            store.dispatch('setMaintenanceMode', [false]);
        }

        const responseJson = await parseResponseBodySafe(response);

        if (expectSuccess && !responseJson?.success) {
            throw createApiError(successErrorMessage, response, responseJson);
        }

        return responseJson;
    }

    if (response.status === 401) {
        if (unauthorized === 'logout') {
            throw await handleUnauthorized();
        }

        const responseJson = await parseResponseBodySafe(response);
        throw new Error(responseJson?.message || 'Unauthorized');
    }

    const responseJson = await parseResponseBodySafe(response);

    if (response.status === 503) {
        store.dispatch('setMaintenanceMode', [true, responseJson?.message]);
        return null;
    }

    if (clearMaintenance) {
        store.dispatch('setMaintenanceMode', [false]);
    }

    throw createApiError(getErrorMessage(response, responseJson), response, responseJson);
}
